import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import trim from 'lodash/trim'
import {
  DraftBlockType,
  DraftEditorCommand,
  EditorState,
  getDefaultKeyBinding,
  RichUtils,
} from 'draft-js'
import PluginEditor from '@draft-js-plugins/editor/lib/Editor'
import { stateFromHTML } from 'draft-js-import-html'
import { stateToHTML } from 'draft-js-export-html'
import nanoid from 'nanoid'
import { useSpellchecker } from 'builder/hooks/useSpellchecker'
import { handleTextWithBullets, handleTextWithHeaders } from 'builder/utils/richTextUtils'
import { IncorrectWord, StyleTypes, TextAreaProps } from './types'
import { processPhrase, replaceText } from './RichTextToolbar/lib'

const preProcessContent = (content: string) => handleTextWithBullets(handleTextWithHeaders(content))

export const useRichTextAreaActions = (props: TextAreaProps) => {
  const {
    value = '',
    onChange,
    locale = 'en',
    autoFocus,
    isAnimating,
    onAnimationEnd,
    onFocus,
  } = props

  const { spellchecker, supportedLocales } = useSpellchecker()
  const supportsLocale = useCallback(
    (locale: string) => {
      return supportedLocales.includes(locale) && !['hu', 'ro', 'gr'].includes(locale)
    },
    [supportedLocales],
  )
  const [isFocused, setIsFocused] = useState(false)
  const [textLength, setTextLength] = useState(0)
  const [checkedText, setCheckedText] = useState('')
  const [isSpellCheckerEnabled, setIsSpellCheckerEnabled] = useState(supportsLocale(locale))
  const [isSpellCheckerPending, setIsSpellCheckerPending] = useState(false)
  const [isSpellingChecked, setIsSpellingChecked] = useState(false)
  const [isForceRenderComplete, setIsForceRenderComplete] = useState(true)
  const [editorState, setEditorState] = useState(() => {
    const contentState = stateFromHTML(preProcessContent(value))
    return EditorState.createWithContent(contentState)
  })
  const [updatedValue, setUpdatedValue] = useState<string | null>(null)
  const incorrectWordsFromStorage = localStorage.getItem('SPELL_CHECKER_INCORRECT_WORDS')
  const editorsWithSpellCheckFromStorage = localStorage.getItem('EDITORS_WITH_SPELL_CHECK')
  const incorrectWords: IncorrectWord[] = useMemo(
    () => (incorrectWordsFromStorage ? JSON.parse(incorrectWordsFromStorage) : []),
    [incorrectWordsFromStorage],
  )
  const editorsWithSpellCheck: string[] = useMemo(
    () => (editorsWithSpellCheckFromStorage ? JSON.parse(editorsWithSpellCheckFromStorage) : []),
    [editorsWithSpellCheckFromStorage],
  )

  const editorRef = useRef<PluginEditor>(null)
  const editorId = useRef(nanoid())
  const animationPlaceholderRef = useRef<HTMLTextAreaElement>(null)

  // Needed to re-apply decorators
  const forceRender = useCallback(() => {
    const contentState = editorState.getCurrentContent()
    const newEditorStateInstance = EditorState.createWithContent(
      contentState,
      editorState.getDecorator(),
    )
    const copyOfEditorState = EditorState.set(newEditorStateInstance, {
      selection: editorState.getSelection(),
      undoStack: editorState.getUndoStack(),
      redoStack: editorState.getRedoStack(),
      lastChangeType: editorState.getLastChangeType(),
    })
    setEditorState(copyOfEditorState)
  }, [editorState])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkSpelling = useCallback(
    debounce((text: string) => {
      // we should check it one more time because something could changed during the debaunce time
      if (!trim(text).length || text === checkedText) {
        setIsSpellCheckerPending(false)
        return
      }
      setIsForceRenderComplete(false)

      return spellchecker
        .checkSpelling(text, locale)
        .then((result: IncorrectWord[]) => {
          if (result.length > 0) {
            const updatedWords = [...incorrectWords]
            // Only add new suggestions to the local storage
            result.forEach(obj => {
              const existingIndex = updatedWords.findIndex(item => item.word === obj.word)
              if (existingIndex !== -1) {
                // If the word already exists, update it
                updatedWords[existingIndex] = obj
              } else {
                // If the word doesn't exist, add it to the list
                updatedWords.push(obj)
              }
            })
            localStorage.setItem('SPELL_CHECKER_INCORRECT_WORDS', JSON.stringify(updatedWords))
          }
          setCheckedText(text)
          setIsSpellCheckerPending(false)
        })
        .catch(() => {
          setIsSpellCheckerEnabled(false)
          setIsSpellCheckerPending(false)
        })
    }, 2000),
    [],
  )

  const handleCheckSpelling = useCallback(
    (text: string) => {
      if (!isSpellCheckerEnabled || !isFocused) return

      setIsSpellCheckerPending(!(!trim(text).length || text === checkedText))
      setIsSpellingChecked(true)

      checkSpelling(text)
    },
    [checkSpelling, checkedText, isFocused, isSpellCheckerEnabled],
  )

  const checkAndUpdateStateFocus = (prevEditorState: EditorState, nextEditorState: EditorState) => {
    /** This logic handles focus changes in a controlled way
     * It was required because we encountered an issue with cursor jumping to beginning
     * when editor was focused or on pressing backspace. It is only required
     * when typing for the first time in the editor.
     */
    const prevSelection = prevEditorState.getSelection()
    const nextSelection = nextEditorState.getSelection()
    /** This condition is checking if the selection (cursor) has moved
     * from the beginning to the second position,
     * both the starting and ending points are the same,
     * and neither the previous nor the next selection (from editor states) has focus.
     * If satisfies, it modifies the next selection to have focus
     * and prevents the cursor from jumping to the start */
    if (
      prevSelection.getAnchorKey() === nextSelection.getAnchorKey() &&
      prevSelection.getAnchorOffset() === 0 &&
      nextSelection.getAnchorOffset() === 1 &&
      prevSelection.getFocusKey() === nextSelection.getFocusKey() &&
      prevSelection.getFocusOffset() === 0 &&
      nextSelection.getFocusOffset() === 1 &&
      !prevSelection.getHasFocus() &&
      !nextSelection.getHasFocus()
    ) {
      const fixedSelection = nextSelection.merge({ hasFocus: true })
      return EditorState.forceSelection(nextEditorState, fixedSelection)
    }
    return nextEditorState
  }

  const handleChange = useCallback(
    (newStateProp: EditorState) => {
      const newContentState = newStateProp.getCurrentContent()
      const oldContentState = editorState.getCurrentContent()

      const newValue = newContentState.hasText() ? stateToHTML(newContentState) : ''
      const oldValue = oldContentState.hasText() ? stateToHTML(oldContentState) : ''

      const plainText = newContentState.getPlainText()
      const updatedState = checkAndUpdateStateFocus(editorState, newStateProp)
      setEditorState(updatedState)
      setTextLength(plainText.length)

      if (oldValue !== newValue) {
        setUpdatedValue(newValue)

        if (isSpellCheckerEnabled) {
          handleCheckSpelling(plainText)
        }
      }
    },
    [editorState, isSpellCheckerEnabled, handleCheckSpelling],
  )

  // For some reason after update to React 18 we started to face issue
  // when after cutting text the editor the change is reverted immediately.
  // Removing onChange() call from handleChange() method is the only way
  // that I found to fix it
  useEffect(() => {
    if (updatedValue !== null) {
      onChange(updatedValue)
    }
  }, [updatedValue])

  const handleToggleInline = (inlineStyle: StyleTypes) => {
    handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle))
  }

  const handleToggleBlock = (blockType: DraftBlockType) => {
    handleChange(RichUtils.toggleBlockType(editorState, blockType))
  }

  const focusEditor = useCallback(() => {
    editorRef.current?.focus()
  }, [])

  const updateEditorsWithSpellCheck = useCallback(
    (isSpellCheckerEnabled: boolean) => {
      let updatedEditorsWithSpellCheck = [...editorsWithSpellCheck]
      if (isSpellCheckerEnabled && !updatedEditorsWithSpellCheck.includes(editorId.current)) {
        updatedEditorsWithSpellCheck.push(editorId.current)
      } else if (!isSpellCheckerEnabled) {
        updatedEditorsWithSpellCheck = updatedEditorsWithSpellCheck.filter(
          id => id !== editorId.current,
        )
      }
      localStorage.setItem('EDITORS_WITH_SPELL_CHECK', JSON.stringify(updatedEditorsWithSpellCheck))
    },
    [editorsWithSpellCheck],
  )

  const handleToggleSpellChecker = () => {
    if (isSpellCheckerPending) return

    setIsSpellCheckerEnabled(!isSpellCheckerEnabled)
  }

  const shouldHidePlaceholder = useMemo(() => {
    const contentState = editorState.getCurrentContent()
    return contentState.hasText() || contentState.getBlockMap().first().getType() !== 'unstyled'
  }, [editorState])

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  const handleTab = (e: React.KeyboardEvent<Element>) => {
    const maxDepth = 1
    const newEditorState = RichUtils.onTab(e, editorState, maxDepth)
    if (newEditorState !== editorState) {
      handleChange(newEditorState)
    }
  }

  const handleKeyBinding = (event: React.KeyboardEvent<Element>) => {
    if (event.key === 'Tab') {
      handleTab(event)
    }
    return getDefaultKeyBinding(event)
  }

  const handleKeyCommand = (command: DraftEditorCommand, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      handleChange(newState)
      return 'handled'
    }
    return 'not-handled'
  }

  const animateContent = useCallback(
    (ref: RefObject<HTMLTextAreaElement>, content: string, onAnimationComplete: () => void) => {
      if (!content) {
        return () => {}
      }

      const value = content?.replace(/<\/p><p><br><\/p><p>/g, '\n\n').replace(/<[^>]*>/g, '') ?? ''
      const l = value.length
      let i = 0

      requestAnimationFrame(() => {
        if (ref.current) ref.current.value = ''
      })

      const interval = setInterval(() => {
        requestAnimationFrame(() => {
          if (ref.current) {
            const { scrollHeight, scrollTop, clientHeight } = ref.current

            ref.current.value = value.substring(0, i + 1)

            if (scrollHeight - scrollTop > clientHeight) {
              ref.current.scrollTop = ref.current.scrollHeight
            }
          }

          if (i + 1 >= l) {
            onAnimationComplete()
            clearInterval(interval)
          }

          i++
        })
      }, 10)

      return () => clearInterval(interval)
    },
    [],
  )

  const addPhrase = useCallback(
    (item: { phrase: string; isSubphrase?: boolean }, isList: boolean) => {
      handleChange(processPhrase(item, editorState, isList))
    },
    [handleChange, editorState],
  )

  const replacePhrase = useCallback(
    (phrase: string) => {
      handleChange(replaceText(phrase, editorState))
    },
    [handleChange, editorState],
  )

  const getEditorText = useCallback(() => {
    return editorState.getCurrentContent().getPlainText()
  }, [editorState])

  useEffect(() => {
    if (autoFocus) {
      focusEditor()
    }
  }, [autoFocus, focusEditor])

  useEffect(() => {
    updateEditorsWithSpellCheck(isSpellCheckerEnabled)
    setIsForceRenderComplete(false)
  }, [incorrectWords, isSpellCheckerEnabled, updateEditorsWithSpellCheck])

  useEffect(() => {
    if (!isForceRenderComplete) {
      forceRender()
      setIsForceRenderComplete(true)
    }
  }, [isForceRenderComplete, forceRender])

  useEffect(() => {
    setIsSpellCheckerEnabled(supportsLocale(locale))
  }, [locale, supportsLocale])

  useEffect(() => {
    if (isSpellCheckerEnabled && isFocused) {
      const contentState = editorState.getCurrentContent()
      const plainText = contentState.getPlainText()

      // Conditionally trigger the spell check on focus
      const triggerSpellCheck = !!plainText && (isSpellCheckerPending || plainText !== checkedText)
      if (triggerSpellCheck) {
        handleCheckSpelling(plainText)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, isSpellCheckerEnabled])

  useEffect(() => {
    if (isAnimating && value) {
      const terminateAnimation = animateContent(animationPlaceholderRef, value, () => {
        if (onAnimationEnd) onAnimationEnd()
      })

      return () => terminateAnimation()
    }
  }, [animateContent, isAnimating, value, onAnimationEnd])

  return {
    animationPlaceholderRef,
    editorId,
    editorRef,
    editorState,
    isFocused,
    isSpellCheckerEnabled,
    isSpellCheckerPending,
    isSpellingChecked,
    textLength,
    shouldHidePlaceholder,

    focusEditor,
    handleChange,
    handleToggleInline,
    handleToggleBlock,
    handleToggleSpellChecker,
    handleFocus,
    handleBlur,
    handleKeyBinding,
    handleKeyCommand,
    supportsLocale,
    setEditorState,
    addPhrase,
    replacePhrase,
    getEditorText,
  }
}
